// ⚠️ PERFORM CORE — Ne pas modifier sans validation complète
// Connecté à : Edge Function reason
// Utilisé dans : CentralBrainDock, Dashboard
import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useBrainEngine } from "@/hooks/useBrainEngine";
import { usePageContext } from "@/hooks/usePageContext";
import { useGuidedFlow, isGuidedDone, markGuidedDone } from "@/hooks/useGuidedFlow";
import { useCurrentProgram } from "@/hooks/useCurrentProgram";
import { subscribeBrainChatVisibility, isBrainChatHidden } from "@/lib/brainChatVisibility";
import { subscribeBrainChatForeground, isBrainChatForeground } from "@/lib/brainChatForeground";
import { ChatDocked } from "./ChatDocked";
import { ChatFullscreen } from "./ChatFullscreen";

export type ChatState = "closed" | "petit" | "grand";

const STATE_KEY = "brain-chat-state";

function loadState(): ChatState {
  try {
    const v = localStorage.getItem(STATE_KEY) as ChatState;
    if (["petit", "grand"].includes(v)) return v;
  } catch {}
  return "closed";
}

// Global open/toggle function for external triggers (sidebar, header brain icon, etc.)
let globalToggleChat: (() => void) | null = null;
export function openBrainChat() {
  globalToggleChat?.();
}

export function ChatContainer() {
  const [state, setState] = useState<ChatState>(loadState);
  const [forceHidden, setForceHidden] = useState(isBrainChatHidden);
  const [foreground, setForeground] = useState(isBrainChatForeground);
  const pageContext = usePageContext();
  const { messages, loading, send, clearHistory, profile, addGuidedMessage, brainError, clearBrainError } = useBrainEngine();
  const { currentProgramId, loading: programLoading } = useCurrentProgram();
  const guidedFlow = useGuidedFlow(profile);
  const navigate = useNavigate();
  const [guidedStep, setGuidedStep] = useState(0);
  const lastQueryRef = useRef<string>("");

  // Subscribe to brain chat visibility signal
  useEffect(() => {
    return subscribeBrainChatVisibility(setForceHidden);
  }, []);

  // Subscribe to foreground toggle
  useEffect(() => {
    return subscribeBrainChatForeground(setForeground);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STATE_KEY, state);
    } catch {}
  }, [state]);

  // Register global opener
  useEffect(() => {
    globalToggleChat = () => {
      setState((prev) => (prev === "petit" || prev === "grand") ? "closed" : "petit");
    };
    return () => {
      globalToggleChat = null;
    };
  }, []);


  // Auto-trigger guided flow for new users (no program, onboarding completed)
  useEffect(() => {
    if (programLoading || loading) return;
    if (isGuidedDone()) return;
    if (!profile?.completed) return;
    if (currentProgramId) {
      // User already has a program → skip guided flow
      markGuidedDone();
      return;
    }
    if (messages.length > 0) return; // Already has messages

    // Trigger step 1
    const step = guidedFlow.getStep(1);
    addGuidedMessage(step.content, step.actions);
    setGuidedStep(1);
    setState("petit");
  }, [programLoading, loading, profile, currentProgramId, messages.length, guidedFlow, addGuidedMessage]);

  const handleGuidedAction = useCallback(
    (actionKey: string) => {
      const result = guidedFlow.getNextStep(actionKey);

      if (result.type === "done") {
        markGuidedDone();
        // Add closing message (step 8) if not already at step 8
        if (guidedStep < 8) {
          const step8 = guidedFlow.getStep(8);
          addGuidedMessage(step8.content, step8.actions);
          setGuidedStep(8);
        }
        return;
      }

      if (result.type === "ask") {
        // Let user type freely — remove actions from last message
        markGuidedDone();
        return;
      }

      if (result.type === "navigate") {
        markGuidedDone();
        navigate(result.route);
        return;
      }

      if (result.type === "step") {
        const step = guidedFlow.getStep(result.step);
        addGuidedMessage(step.content, step.actions);
        setGuidedStep(result.step);

        if (result.step >= 8) {
          markGuidedDone();
        }
      }
    },
    [guidedFlow, guidedStep, addGuidedMessage, navigate]
  );

  const handleSend = useCallback(
    (text: string) => {
      if (!isGuidedDone()) markGuidedDone();
      lastQueryRef.current = text;
      clearBrainError();
      const mode = state === "grand" ? "full" : "dock";
      send(text, pageContext, mode);
    },
    [state, send, pageContext, clearBrainError]
  );

  const handleRetry = useCallback(() => {
    if (lastQueryRef.current) {
      clearBrainError();
      const mode = state === "grand" ? "full" : "dock";
      send(lastQueryRef.current, pageContext, mode);
    }
  }, [state, send, pageContext, clearBrainError]);

  // Listen for brain-inject-prompt (from StatusActionPanel)
  useEffect(() => {
    const handler = (e: Event) => {
      const prompt = (e as CustomEvent).detail?.prompt;
      if (prompt && typeof prompt === "string") {
        handleSend(prompt);
      }
    };
    window.addEventListener("brain-inject-prompt", handler);
    return () => window.removeEventListener("brain-inject-prompt", handler);
  }, [handleSend]);

  if (state === "closed" || forceHidden) return null;

  const zClass = foreground ? "z-[9999]" : "";

  if (state === "petit") {
    return (
      <div className={zClass}>
        <ChatDocked
          messages={messages}
          loading={loading}
          pageContext={pageContext}
          onSend={handleSend}
          onMinimize={() => setState("closed")}
          onMaximize={() => setState("grand")}
          onClose={() => setState("closed")}
          onClear={clearHistory}
          onActionClick={handleGuidedAction}
          brainError={brainError}
          onRetry={handleRetry}
        />
      </div>
    );
  }

  // grand
  return (
    <div className={zClass}>
      <ChatFullscreen
        messages={messages}
        loading={loading}
        profile={profile}
        pageContext={pageContext}
        onSend={handleSend}
        onMinimize={() => setState("closed")}
        onClose={() => setState("closed")}
        onClear={clearHistory}
        onActionClick={handleGuidedAction}
        brainError={brainError}
        onRetry={handleRetry}
      />
    </div>
  );
}
